import { useEffect, useRef } from 'react';

const CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_SIGNIN_CLIENT_ID as string | undefined;
const GIS_SRC = 'https://accounts.google.com/gsi/client';

type GisId = {
  initialize: (config: {
    client_id: string;
    callback: (resp: { credential: string }) => void;
  }) => void;
  renderButton: (element: HTMLElement, options: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    google?: { accounts?: { id?: GisId } };
  }
}

let scriptLoad: Promise<void> | null = null;

function loadGis(): Promise<void> {
  if (scriptLoad) return scriptLoad;
  scriptLoad = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptLoad = null;
      reject(new Error('Failed to load Google Sign-In'));
    };
    document.head.appendChild(s);
  });
  return scriptLoad;
}

interface Props {
  onCredential: (credential: string) => void | Promise<void>;
  onError?: (message: string) => void;
}

export function GoogleSignInButton({ onCredential, onError }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !ref.current) return;
        const gis = window.google?.accounts?.id;
        if (!gis) return;
        gis.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => {
            if (resp.credential) void onCredential(resp.credential);
          },
        });
        gis.renderButton(ref.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: 320,
        });
      })
      .catch((err) => {
        if (!cancelled) onError?.(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [onCredential, onError]);

  if (!CLIENT_ID) return null;
  return <div ref={ref} className="google-signin-button" />;
}
